import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { GetEnterpriseRiskByIdResponseRow } from '@risksmart-app/trpc/types/backend/v1/enterprise-risk.types';
import type { GetEnterpriseRiskByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useIsFeatureVisibleToOrg } from 'src/utils/featureFlags';
import { useTRPC } from 'src/utils/trpc';

/**
 * Maps TRPC enterprise risks data to match the GraphQL query structure
 */
export function mapTrpcEnterpriseRisksToGraphQL(
  trpcData: GetEnterpriseRiskByIdResponseRow[] | undefined
): GetEnterpriseRiskByIdQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    enterprise_risk: trpcData,
  };
}

export const useGetEnterpriseRiskByIdTRPC = (id: string, skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.enterpriseRisk.getById.queryOptions({ id }),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcEnterpriseRisksToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
